import type { AnchorHTMLAttributes, ImgHTMLAttributes } from 'react';
import ReactMarkdown, { type Components, type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/format.ts';

/**
 * Agent-authored prose, rendered as Markdown.
 *
 * Agent text is untrusted, so what this does *not* do matters as much as
 * what it does: `react-markdown` walks the parsed Markdown AST and builds
 * React elements directly from it, never through `dangerouslySetInnerHTML` —
 * so raw HTML in the source (`<img onerror=…>`, `<script>`, a pasted `<style>`
 * block) is not parsed as markup at all. It prints as the literal text. There
 * is deliberately no `rehype-raw` plugin wired in here: that is the one
 * plugin that would turn markdown back into an HTML sink, which is exactly
 * what agent-authored text should never get.
 *
 * Sizing is `em`-relative throughout (see `.rg-markdown` in theme.css) rather
 * than fixed — it inherits whatever font size the caller's bubble/row already
 * set, so the same component reads right in a 360px chat column and a wider
 * comment thread without a size prop to keep in sync by hand.
 */
export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cn('rg-markdown', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Links open in a new tab without handing the destination a `window.opener`
 * back into this app — `noopener` closes that, `noreferrer` also drops the
 * `Referer` header, which matters here because agent text can point anywhere.
 */
function Link({
  href,
  children,
  node: _node,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & ExtraProps) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

/**
 * Markdown images are `<img src>` pointed at whatever URL the source names —
 * rendering one fires a real browser request the moment the message renders,
 * which is a tracking-pixel-shaped hole for text nobody has vetted. Nothing
 * in the brief for this task asked for inline images, so instead of fetching
 * anything automatically this renders as a plain link, same as any other URL
 * a person has to choose to open.
 */
function ImageAsLink({ src, alt }: ImgHTMLAttributes<HTMLImageElement> & ExtraProps) {
  if (!src) return alt ? <>{alt}</> : null;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      🖼 {alt || src}
    </a>
  );
}

const components: Components = {
  a: Link,
  img: ImageAsLink,
};
